import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';

import { buildBookableGroups } from '@elara/client-core/lib/bookable-groups';
import { pluralize } from '@elara/client-core/lib/utils';

import { useNavigate } from '#states/hooks.js';
import { Button } from '#ui/button.js';
import { CoverImage } from '#ui/cards/DetailCardLayout.js';
import { AirlineLogo } from '#ui/cards/FlightCard.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '#ui/dialog.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

interface BookTripModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  items: ItineraryItem[];
}

export function BookTripModal({ open, onOpenChange, tripId, items }: BookTripModalProps) {
  const { navigate } = useNavigate();
  const { flights, hotels } = buildBookableGroups(items);
  const hasFlights = flights.length > 0;
  const hasHotels = hotels.length > 0;

  // Navigate to checkout in the current tab (matching every other book button in
  // the app), closing this modal first so it isn't left open behind the checkout
  // page or when the user navigates back. Previously this used
  // `window.open(url, '_blank')`, which opened checkout in a bare new tab with no
  // SPA history — leaving a blank page after the booking completed (ELARA-851).
  const goToCheckout = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Book your trip</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          {hasFlights && (
            <section className="flex flex-col gap-2">
              <div className="text-xs font-medium text-muted-foreground">Flights</div>
              <div className="flex flex-col gap-2">
                {flights.map(({ itemId, referenceId, title, airline, departDate, returnDate }) => (
                  <BookableRow
                    key={itemId}
                    thumbnail={<AirlineLogo airline={airline} className="w-9 h-9" />}
                    title={title}
                    subtitle={
                      departDate && returnDate ? `${departDate} → ${returnDate}` : departDate ? departDate : undefined
                    }
                    onBook={() => goToCheckout(`/checkout/flight/${tripId}/${referenceId}`)}
                  />
                ))}
              </div>
            </section>
          )}

          {hasHotels && (
            <section className="flex flex-col gap-2">
              <div className="text-xs font-medium text-muted-foreground">Hotels</div>
              <div className="flex flex-col gap-2">
                {hotels.map(({ stayKey, hotelName, dateRange, roomItemIds, imageUrl }) => (
                  <BookableRow
                    // One row per stay: the same hotel on two date ranges is two checkouts (ELARA-1087).
                    key={stayKey}
                    thumbnail={
                      <CoverImage imageUrls={imageUrl ? [imageUrl] : undefined} type="hotel" className="w-9 h-9 rounded-sm flex-shrink-0" />
                    }
                    title={hotelName}
                    subtitle={[dateRange, `${roomItemIds.length} ${pluralize(roomItemIds.length, 'room')}`]
                      .filter((part): part is string => typeof part === 'string' && part.length > 0)
                      .join(' · ')}
                    onBook={() => goToCheckout(`/checkout/hotel/${tripId}?items=${roomItemIds.join(',')}`)}
                  />
                ))}
              </div>
            </section>
          )}

          {!hasFlights && !hasHotels && (
            <p className="text-sm text-muted-foreground py-4">Nothing left to book. Enjoy your trip!</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookableRow({
  thumbnail,
  title,
  subtitle,
  onBook,
}: {
  thumbnail: React.ReactNode;
  title: string;
  subtitle?: string;
  onBook: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5">
      <div className="flex-shrink-0">{thumbnail}</div>
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-medium truncate">{title}</span>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
      <Button size="sm" className="flex-shrink-0" onClick={onBook}>
        <MaterialIcon icon="shopping_cart_checkout" size={IconSizes.sm} />
        Book
      </Button>
    </div>
  );
}
